import { useCallback, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/endpoints.js';
import {
  refreshAccessToken,
  setAccessToken,
  setUnauthorizedHandler,
} from '../api/client.js';
import { AuthContext } from './AuthContext.js';

/**
 * Holds the signed-in user. The access token itself never enters React state —
 * it lives in the api client module, so it cannot end up in a devtools dump, a
 * serialised error report, or a component that re-renders it into the DOM.
 *
 * `status` is a three-way rather than a boolean because "we do not know yet"
 * and "definitely signed out" must not look the same: treating the first as the
 * second bounces a signed-in staff member to the login page on every refresh.
 */
export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading');

  const signOutLocally = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('signed-out');
  }, []);

  // A 401 that survived the client's own refresh attempt means the session is
  // genuinely over — most often the refresh token expired overnight.
  useEffect(() => {
    setUnauthorizedHandler(signOutLocally);
    return () => setUnauthorizedHandler(null);
  }, [signOutLocally]);

  /*
   * On mount, try to trade the httpOnly refresh cookie for an access token.
   * For a visitor who has never signed in this is one 401 and nothing more.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = await refreshAccessToken();

      if (cancelled) return;
      if (!token) {
        setStatus('signed-out');
        return;
      }

      try {
        const me = await authApi.me();
        if (cancelled) return;
        setUser(me);
        setStatus('signed-in');
      } catch {
        if (!cancelled) signOutLocally();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [signOutLocally]);

  const signIn = useCallback(async (credentials) => {
    const result = await authApi.login(credentials);
    setAccessToken(result.accessToken);
    setUser(result.user);
    setStatus('signed-in');
    return result.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // The local session ends either way; a failed call just leaves a row on
      // the server that expires on its own.
    }
    signOutLocally();
  }, [signOutLocally]);

  const value = useMemo(
    () => ({
      user,
      status,
      isSignedIn: status === 'signed-in',
      isAdmin: user?.role === 'admin',
      signIn,
      signOut,
    }),
    [user, status, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
